import { sessionStore } from '@/core/auth/SessionStore'
import { isNative } from '@/core/platform/runtime'

/**
 * Wrapper de `fetch` com:
 *  - Injeção automática do `Authorization: Bearer <token>` (da SessionStore).
 *  - 401 → marca o token como vencido e dispara `monitor:reauth`. NÃO apaga a
 *    sessão: o operador segue lançando offline e entra de novo quando puder.
 *  - Suporte a `If-None-Match` para catálogos.
 *  - Erros tipados (ApiError) com `status` e `code` quando o backend retorna estrutura.
 *
 * NÃO faz retry — outbox cuida disso. Para mutações que precisam idempotência, o caller
 * gera UUID v7 antes de chamar.
 *
 * API_BASE em 2 ambientes:
 *  - Web/PWA: `/api` (default) → Cloudflare Pages Function reescreve pro Fly.io
 *  - Native (Capacitor APK): URL absoluta direto pra `infrawork.pages.dev/api`,
 *    continuando a usar a Pages Function. Mesma chain, mesma CORS policy, mesma
 *    assinatura de tokens.
 *  - VITE_API_BASE_URL pode sobrescrever em ambos (útil pra dev/staging).
 */

const PROD_BASE = 'https://infrawork.pages.dev/api'

const API_BASE = import.meta.env.VITE_API_BASE_URL
  ?? (isNative() ? PROD_BASE : '/api')

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized() {
    return this.status === 401
  }
  get isNotModified() {
    return this.status === 304
  }
  get isOffline() {
    return this.status === 0
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown
  headers?: Record<string, string>
  ifNoneMatch?: string
  /** Por padrão fetchClient parseia JSON; passe `false` para receber Response cru (multipart download). */
  parseJson?: boolean
  /**
   * Tempo máximo da chamada inteira (headers + corpo). Default: 20s em GET,
   * 90s em POST/PUT JSON, 5min em upload (FormData/Blob).
   *
   * Por quê: com sinal fraco ("1 tracinho") o fetch não falha — fica pendurado
   * por minutos. As telas ficavam em "Carregando…" sem nunca cair no cache
   * local. Estourou o tempo = tratamos como offline (status 0) e o chamador usa
   * o que tem guardado. Nos POSTs o prazo é folgado de propósito: abortar cedo
   * demais um envio que o servidor já gravou gera reenvio.
   */
  timeoutMs?: number
}

export interface ApiResponse<T> {
  data: T
  etag: string | null
  status: number
}

async function call<T>(path: string, opts: RequestOptions = {}): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...sessionStore.authHeader(),
    ...(opts.headers ?? {}),
  }
  if (opts.ifNoneMatch) headers['If-None-Match'] = opts.ifNoneMatch

  let body: BodyInit | undefined
  if (opts.body !== undefined && opts.body !== null) {
    if (opts.body instanceof FormData || opts.body instanceof Blob) {
      // Não setar Content-Type — browser adiciona com boundary correto (crítico iOS Safari)
      body = opts.body
    } else {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(opts.body)
    }
  }

  const method = (opts.method ?? 'GET').toUpperCase()
  const timeoutMs =
    opts.timeoutMs ?? (body instanceof FormData || body instanceof Blob ? 300_000 : method === 'GET' ? 20_000 : 90_000)
  const ctrl = new AbortController()
  let estourou = false
  const timer = setTimeout(() => {
    estourou = true
    ctrl.abort()
  }, timeoutMs)
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort()
    else opts.signal.addEventListener('abort', () => ctrl.abort(), { once: true })
  }
  const offline = (err: unknown) =>
    new ApiError(
      0,
      estourou ? 'Tempo esgotado — rede lenta ou sem sinal' : err instanceof Error ? err.message : 'Erro de rede',
      estourou ? 'TIMEOUT' : 'NETWORK_ERROR',
    )

  try {
    return await callComPrazo<T>(path, opts, headers, body, ctrl.signal, offline)
  } finally {
    clearTimeout(timer)
  }
}

async function callComPrazo<T>(
  path: string,
  opts: RequestOptions,
  headers: Record<string, string>,
  body: BodyInit | undefined,
  signal: AbortSignal,
  offline: (err: unknown) => ApiError,
): Promise<ApiResponse<T>> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, { ...opts, headers, body, signal })
  } catch (err) {
    // Rede caiu / DNS / CORS / prazo estourado — tratamos como offline
    throw offline(err)
  }

  const etag = res.headers.get('ETag')

  if (res.status === 304) {
    return { data: undefined as T, etag, status: 304 }
  }

  if (res.status === 401) {
    // Login com senha errada também é 401 — não mexe na sessão que já existe.
    if (!path.startsWith('/auth/login')) {
      sessionStore.markTokenExpired()
      window.dispatchEvent(new CustomEvent('monitor:reauth'))
    }
    throw new ApiError(401, 'Sessão expirada', 'UNAUTHORIZED')
  }

  if (!res.ok) {
    let code: string | undefined
    let details: unknown
    let message = `HTTP ${res.status}`
    try {
      const errBody = (await res.json()) as { error?: { code?: string; message?: string; details?: unknown } }
      if (errBody.error) {
        code = errBody.error.code
        message = errBody.error.message ?? message
        details = errBody.error.details
      }
    } catch {
      // resposta não era JSON — mantém mensagem default
    }
    throw new ApiError(res.status, message, code, details)
  }

  let data: T
  if (opts.parseJson === false) {
    data = res as unknown as T
  } else if (res.status === 204) {
    data = undefined as T
  } else {
    try {
      data = (await res.json()) as T
    } catch (err) {
      // Conexão caiu (ou prazo estourou) no meio do corpo — é rede, não é resposta inválida.
      throw offline(err)
    }
  }

  return { data, etag, status: res.status }
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'body'>) =>
    call<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    call<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    call<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    call<T>(path, { ...opts, method: 'DELETE' }),
}
